import {
  type HomeworkAssignment,
  type HomeworkRecord,
  type HomeworkStatus,
  parseAssignmentScores,
  type Student,
} from "@classroom/domain";
import { useState } from "react";
import { useTranslation } from "./i18n";
import { useDialogFocus } from "./lib/accessibility";
import { assignmentRecordLabels } from "./lib/assignmentRecordLabels";
import { useSaveBeforeUnload } from "./lib/useSaveBeforeUnload";
import "./teacher-workflows.css";

export default function AssignmentBatchDialog({
  assignment,
  students,
  draft,
  readOnly,
  onApply,
  onClose,
}: {
  assignment: HomeworkAssignment;
  students: Student[];
  draft: HomeworkRecord["students"];
  readOnly: boolean;
  onApply: (patches: HomeworkRecord["students"]) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation("assignmentRecords");
  const labels = assignmentRecordLabels(assignment.category);
  const [tab, setTab] = useState<"status" | "scores">("status");
  const [scope, setScope] = useState("unrecorded"),
    [status, setStatus] = useState<HomeworkStatus>("complete");
  const [text, setText] = useState(""),
    [format, setFormat] = useState<"order" | "number">("order");
  const dirty = Boolean(text.trim()) || scope !== "unrecorded" || status !== "complete";
  const close = () => {
    if (!dirty || window.confirm(t("batch.discard"))) onClose();
  };
  const ref = useDialogFocus(true, close);
  useSaveBeforeUnload(dirty);
  const unrecorded = students.filter((student) => !draft[student.id]);
  const selected = scope === "unrecorded" ? unrecorded : students;
  const rows = parseAssignmentScores(text, students, format, assignment.maxScore);
  const validRows = rows.filter((row) => row.studentId && row.score !== undefined && !row.error);
  const errors = rows.filter((row) => row.error);
  function apply() {
    if (readOnly) return;
    const patches: HomeworkRecord["students"] = {};
    if (tab === "status")
      for (const student of selected) patches[student.id] = { ...(draft[student.id] || { status }), status };
    else {
      if (errors.length || !validRows.length) return;
      for (const row of validRows)
        patches[row.studentId!] = { ...(draft[row.studentId!] || { status: "complete" }), score: row.score };
    }
    onApply(patches);
    onClose();
  }
  return (
    <div className="assignment-folder-overlay">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={t("batch.title")}
        className="paper-card workflow-dialog workflow-dialog-wide"
        tabIndex={-1}
        data-autosave
        data-save-state={dirty ? "dirty" : "saved"}
      >
        <header className="workflow-dialog-heading">
          <div>
            <h3>{t("batch.title")}</h3>
            <p className="workflow-context">{assignment.title}</p>
          </div>
          <button type="button" aria-label={t("batch.close")} onClick={close}>
            ×
          </button>
        </header>
        <div className="workflow-segments" role="group" aria-label={t("batch.title")}>
          <button type="button" aria-pressed={tab === "status"} onClick={() => setTab("status")}>
            {t("batch.statusTab")}
          </button>
          <button type="button" aria-pressed={tab === "scores"} onClick={() => setTab("scores")}>
            {t("batch.scoresTab")}
          </button>
        </div>
        {tab === "status" ? (
          <div className="workflow-fields">
            <label>
              {t("batch.scope")}
              <select
                data-dialog-initial-focus
                aria-label={t("batch.scope")}
                value={scope}
                onChange={(event) => setScope(event.target.value)}
              >
                <option value="unrecorded">{t("batch.unrecorded", { count: unrecorded.length })}</option>
                <option value="all">{t("batch.everyone", { count: students.length })}</option>
              </select>
            </label>
            <label>
              {t("batch.status")}
              <select
                aria-label={t("batch.status")}
                value={status}
                onChange={(event) => setStatus(event.target.value as HomeworkStatus)}
              >
                {Object.entries(labels.statuses).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <p className="workflow-context">{t("batch.replaceStatus")}</p>
          </div>
        ) : (
          <div className="workflow-fields">
            <label>
              {t("batch.format")}
              <select
                aria-label={t("batch.format")}
                value={format}
                onChange={(event) => setFormat(event.target.value as "order" | "number")}
              >
                <option value="order">{t("batch.order")}</option>
                <option value="number">{t("batch.number")}</option>
              </select>
            </label>
            <label>
              {t("batch.text")}
              <textarea
                aria-label={t("batch.text")}
                rows={5}
                value={text}
                spellCheck={false}
                onChange={(event) => setText(event.target.value)}
                aria-describedby="batch-paste-hint"
              />
            </label>
            <p id="batch-paste-hint" className="workflow-context">
              {t(format === "order" ? "batch.orderHint" : "batch.numberHint")}
            </p>
            {rows.length > 0 && (
              // biome-ignore lint/a11y/noNoninteractiveTabindex: Keyboard users need to scroll the score preview.
              <div className="workflow-preview" role="region" aria-label={t("batch.preview")} tabIndex={0}>
                <table>
                  <thead>
                    <tr>
                      <th>{t("batch.student")}</th>
                      <th>{t("batch.previous")}</th>
                      <th>{t("batch.next")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.line}>
                        <th scope="row">{row.label || row.line}</th>
                        <td>{row.studentId ? (draft[row.studentId]?.score ?? "—") : "—"}</td>
                        <td>
                          {row.error ? (
                            <span className="form-error">{t(`batch.errors.${row.error}`)}</span>
                          ) : (
                            (row.score ?? t("batch.skipped"))
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {errors.length > 0 && (
              <p role="alert" className="form-error">
                {t("batch.errorLine", { line: errors[0].line, error: t(`batch.errors.${errors[0].error!}`) })}
              </p>
            )}
            {validRows.length > 0 && (
              <p className="workflow-context">
                {t("batch.replaceScores", {
                  count: validRows.length,
                  existing: validRows.filter((row) => draft[row.studentId!]?.score !== undefined).length,
                })}
              </p>
            )}
          </div>
        )}
        <div className="assignment-folder-dialog-actions">
          <button type="button" onClick={close}>
            {t("batch.cancel")}
          </button>
          <button
            type="button"
            className="primary-action"
            disabled={readOnly || (tab === "status" ? !selected.length : !validRows.length || errors.length > 0)}
            onClick={apply}
          >
            {tab === "status"
              ? t("batch.applyStatus", { count: selected.length })
              : t("batch.applyScores", { count: validRows.length })}
          </button>
        </div>
      </div>
    </div>
  );
}
